import React, { useState, useEffect, useCallback, useRef } from 'react';
import './NotificationBell.css';
import { useAuth } from '../context/AuthContext';

const NotificationBell = ({ language = 'he', onViewAll }) => {
  const { authFetch } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [urgentCount, setUrgentCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  const t = {
    he: {
      notifications: 'התראות',
      noNotifications: 'אין התראות חדשות',
      markAllRead: 'סמן הכל כנקרא',
      viewAll: 'צפה בכל ההתראות',
      justNow: 'ממש עכשיו',
      minutesAgo: 'דקות',
      hoursAgo: 'שעות',
      daysAgo: 'ימים',
      appointment_reminder: 'תזכורת תור',
      appointment_confirmation: 'אישור תור',
      appointment_cancelled: 'תור בוטל',
      new_patient: 'מטופל חדש',
      missed_call: 'שיחה שלא נענתה',
      follow_up_due: 'מעקב נדרש',
      system_alert: 'התראת מערכת',
      sms_sent: 'SMS נשלח',
      sms_failed: 'SMS נכשל',
      patient_message: 'הודעה ממטופל',
    },
    en: {
      notifications: 'Notifications',
      noNotifications: 'No new notifications',
      markAllRead: 'Mark all as read',
      viewAll: 'View all notifications',
      justNow: 'Just now',
      minutesAgo: 'min ago',
      hoursAgo: 'hours ago',
      daysAgo: 'days ago',
      appointment_reminder: 'Appointment Reminder',
      appointment_confirmation: 'Appointment Confirmed',
      appointment_cancelled: 'Appointment Cancelled',
      new_patient: 'New Patient',
      missed_call: 'Missed Call',
      follow_up_due: 'Follow-up Due',
      system_alert: 'System Alert',
      sms_sent: 'SMS Sent',
      sms_failed: 'SMS Failed',
      patient_message: 'Patient message',
    }
  }[language];

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await authFetch('/api/notifications/unread-count');
      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.unread_count || 0);
        setUrgentCount(data.urgent_count || 0);
      }
    } catch (err) {
      console.error('Error fetching notification count:', err);
    }
  }, [authFetch]);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const response = await authFetch(
        `/api/notifications?language=${language}&limit=10`
      );
      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unread_count || 0);
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [language, authFetch]);

  // Initial load and polling
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000); // Poll every 30s
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Fetch full list when dropdown opens
  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, fetchNotifications]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      await authFetch(`/api/notifications/${notificationId}/read`, {
        method: 'PUT'
      });
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      await authFetch('/api/notifications/read-all', {
        method: 'PUT'
      });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  // Dismiss notification
  const dismissNotification = async (notificationId, e) => {
    e.stopPropagation();
    try {
      await authFetch(`/api/notifications/${notificationId}/dismiss`, {
        method: 'PUT'
      });
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (err) {
      console.error('Error dismissing notification:', err);
    }
  };

  // Format time ago
  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const date = new Date(timestamp);
    const diff = Math.floor((now - date) / 1000);

    if (diff < 60) return t.justNow;
    if (diff < 3600) return `${Math.floor(diff / 60)} ${t.minutesAgo}`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} ${t.hoursAgo}`;
    return `${Math.floor(diff / 86400)} ${t.daysAgo}`;
  };

  // Get icon for notification type
  const getIcon = (type) => {
    const icons = {
      appointment_reminder: '⏰',
      appointment_confirmation: '✅',
      appointment_cancelled: '❌',
      new_patient: '👤',
      missed_call: '📞',
      follow_up_due: '📋',
      system_alert: '⚠️',
      sms_sent: '📱',
      sms_failed: '📵',
      patient_message: '📨',
    };
    return icons[type] || '🔔';
  };

  // Get color for priority
  const getPriorityClass = (priority) => {
    const classes = {
      urgent: 'priority-urgent',
      high: 'priority-high',
      normal: 'priority-normal',
      low: 'priority-low',
    };
    return classes[priority] || '';
  };

  return (
    <div className="notification-bell" ref={dropdownRef}>
      <button
        className={`bell-button ${urgentCount > 0 ? 'has-urgent' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label={t.notifications}
      >
        <span className="bell-icon">🔔</span>
        {unreadCount > 0 && (
          <span className="notification-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className={`notification-dropdown ${language === 'he' ? 'rtl' : 'ltr'}`}>
          <div className="dropdown-header">
            <h3>{t.notifications}</h3>
            {unreadCount > 0 && (
              <button className="mark-read-btn" onClick={markAllAsRead}>
                {t.markAllRead}
              </button>
            )}
          </div>

          <div className="notification-list">
            {loading ? (
              <div className="notification-loading">
                <div className="spinner"></div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="no-notifications">
                <span className="empty-icon">🔔</span>
                <p>{t.noNotifications}</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`notification-item ${!notification.is_read ? 'unread' : ''} ${getPriorityClass(notification.priority)}`}
                  onClick={() => !notification.is_read && markAsRead(notification.id)}
                >
                  <div className="notification-icon">
                    {getIcon(notification.type)}
                  </div>
                  <div className="notification-content">
                    <div className="notification-title">
                      {notification.title}
                    </div>
                    {notification.message && (
                      <div className="notification-message">
                        {notification.message}
                      </div>
                    )}
                    <div className="notification-time">
                      {formatTimeAgo(notification.created_at)}
                      {notification.patient_name && (
                        <span className="patient-tag"> • {notification.patient_name}</span>
                      )}
                    </div>
                  </div>
                  <button
                    className="dismiss-btn"
                    onClick={(e) => dismissNotification(notification.id, e)}
                    title="Dismiss"
                  >
                    ×
                  </button>
                </div>
              ))
            )}
          </div>

          {notifications.length > 0 && (
            <div className="dropdown-footer">
              <button
                className="view-all-btn"
                onClick={() => {
                  setIsOpen(false);
                  if (onViewAll) {
                    onViewAll();
                  }
                }}
              >
                {t.viewAll}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
